import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';   // Record 588 (ruling 202)
import { LADDER } from '../data/claimLadder';
import { claimCommittee, claimBasisLabel, claimFiledAtSeconds } from '../data/claimPayload';
import { lateArrivalFlag } from '../data/claimArrival';   // Record 680 (audit 3.6): the late-arrival flag on the row
import { subscribeVisibleClaims } from '../data/claimQueries';
import { LC_ROSTER } from '../data/lcRoster';
import MoreSection from '../components/MoreSection';   // ruling 276, screen 3: filtering by stage behind More

// The Local Chair is a PROJECTION of the claim's own scopeKey through the roster -- never the
// filer's name. `adjuster` is the person who filed; printing it under a union office asserts an
// authority that does not exist (PD-4). A vacant or unattested seat must say so.
function localChairFor(claim) {
  const sk = String(claim && claim.scopeKey ? claim.scopeKey : '');
  if (!sk || sk.endsWith('|UNROUTED')) return 'Unrouted';
  const hit = LC_ROSTER.find(lc => lc.scopeKey === sk);
  if (!hit) return sk;
  const inParens = hit.label.match(/\(([^)]*)\)\s*$/);
  return inParens ? inParens[1] : hit.label;
}

// UNHELD is NOT the same condition as UNROUTED and is deliberately not folded into it.
//
//   UNROUTED  the work location resolved to no scopeKey at all. The remedy is to MAP A LOCATION.
//   UNHELD    the scopeKey resolved perfectly -- to a seat that nobody sits in. The remedy is to
//             APPOINT A PERSON, or for the GC to re-route the claim by hand.
//
// Merging them would tell an officer that eighteen claims need a map fix when what they need is a
// Local Chairperson. Two conditions, two remedies, two filters.
//
// WHY THIS EXISTS AT ALL: a claim routed to a vacant seat carries routingStatus 'routed', so it
// appears in NO LC's list, does NOT show under Unrouted, and sits in the GC's committee view
// undifferentiated from claims that are actually being worked. It looks handled and is not.
function isUnheldSeat(claim) {
  const sk = String(claim && claim.scopeKey ? claim.scopeKey : '');
  if (!sk || sk.endsWith('|UNROUTED')) return false;   // that is UNROUTED, a different thing
  const hit = LC_ROSTER.find(lc => lc.scopeKey === sk);
  if (!hit) return false;
  // VACANT ONLY -- deliberately NOT 'NOT OF RECORD', and the distinction is the whole point.
  //
  //   VACANT         nobody sits in the seat. No LC account holds this scopeKey, so no human will
  //                  ever see the claim. THIS is the routing hole.
  //   NOT OF RECORD  the platform does not record WHO holds the seat (ruling 54 placeholder). The
  //                  seat may well be held: routing depends on an LC account carrying the scopeKey
  //                  in its `scopes`, not on whether the roster prints a name. An attestation gap
  //                  is not a routing hole.
  //
  // Folding NOT OF RECORD in here would mark 10 of 11 seats Unheld and make the filter useless at
  // exactly the moment it needs to point at the one seat that is genuinely empty.
  return /\(VACANT\b/.test(hit.label);
}

// Whole days between a Firestore timestamp (seconds) and now. Returns null when unknown rather
// than 0 -- PD-4: an unknown age is not an age of zero.
function daysSince(seconds) {
  if (!seconds) return null;
  return Math.floor((Date.now() / 1000 - seconds) / 86400);
}

// Whole days from today to an ISO yyyy-mm-dd deadline. Negative means the date has passed.
// expiresAt is written by computeExpiresAt() ONLY when the agreement is catalogued and a date of
// occurrence exists; it is ABSENT otherwise, and an absent deadline stays absent here.
function daysUntilISO(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(String(iso))) return null;
  const d = new Date(String(iso) + 'T00:00:00');
  if (isNaN(d.getTime())) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86400000);
}

// 'Draft' and 'Archived' were absent from this row while being present as stored statuses, so a
// draft or an archived claim could only be seen under 'All' and could never be isolated or
// excluded. A status the platform writes must be a status the officer can filter on.
//
// R-6b, 2026-09-21: AND IT HAD HAPPENED AGAIN. `gc_review` - the hinge of the whole ladder, the rung
// at which a claim leaves the Local Chairperson and becomes the General Chairperson's - was written
// by the platform, shown on the stage bar, and MISSING FROM THIS ROW. The one queue a General
// Chairperson most needs to isolate was the one he could not filter to. The rule above was right and
// was broken by the next stage anyone added.
//
// So the list is no longer typed here. It is DERIVED from src\data\claimLadder.js - the same LADDER
// the stage bar renders and lcMove() enforces - and a rung added there appears here by itself
// (directives section XI). Two hand-maintained lists of the same statuses is the defect, not the
// symptom; this removes the second list rather than correcting it a third time.
const OFF_LADDER_FILTERS = ['Archived'];

const STAGES = ['All', 'Unrouted', 'Unheld']
  .concat(LADDER.map(s => s.label))
  .concat(OFF_LADDER_FILTERS);
// RULING 276, SCREEN 3 (2026-10-16): this page's one job is "see which claim needs me next". The three filters that serve it
// stay in view; filtering by stage is used rarely and sits behind More - nothing is removed, and it opens by itself when the
// page arrives already filtered by a stage.
const NEXT_FILTERS = ['All', 'Unrouted', 'Unheld'];
const STAGE_FILTERS = STAGES.filter(s => NEXT_FILTERS.indexOf(s) === -1);

const STATUS_KEYS = Object.assign(
  { 'All': null, 'Archived': 'archived' },
  Object.fromEntries(LADDER.map(s => [s.label, s.key])),
);

export default function Claims({ user, onNavigate, onViewClaim, initialFilter }) {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [filter, setFilter] = useState(initialFilter || 'All');
  const [search, setSearch] = useState('');

  // A Local Chairperson whose account carries no scope list falls through to the member branch of
  // the query below and sees only claims he filed himself -- with no message and no error. The page
  // then reads "0 claims shown", which he cannot tell from a quiet week. That is the same class of
  // defect the loadError block exists to prevent: a FAILURE THAT RENDERS AS AN EMPTY STATE.
  // The query is deliberately left alone; this only stops the empty result being read as good news.
  const lcNotProvisioned = user?.role === 'local_chair'
    && !(Array.isArray(user?.scopes) && user.scopes.length > 0);

  // Firestore's 'in' operator caps at 30 values, so the query below slices the scope list. No Local
  // Chairperson holds anywhere near 30 scope keys today -- but if one ever does, every claim beyond
  // the thirtieth disappears with no error. That is the same failure shape as lcNotProvisioned
  // above: SILENT TRUNCATION RENDERING AS AN EMPTY RESULT. Say it out loud instead.
  const scopesTruncated = user?.role === 'local_chair'
    && Array.isArray(user?.scopes) && user.scopes.length > 30;

  useEffect(() => {
    // Role-shaped query (single where, no composite index; sorted client-side below):
    //   admin  -> all claims
    //   gc     -> claims in the general chair's committee (includes the UNROUTED bucket)
    //   lc     -> claims whose scopeKey is in the local chair's scope list
    //   member -> the member's own claims
    // F-15 (SS201): the query is shaped in src\data\claimQueries.js (status constraint beside the
    // scope) because the read rule excludes drafts and an unshaped query is refused outright.
    const unsubscribe = subscribeVisibleClaims(db, user, rows => {
      rows.sort((a, b) => claimFiledAtSeconds(b) - claimFiledAtSeconds(a));
      setClaims(rows);
      setLoadError('');
      setLoading(false);
    }, (err) => {
      console.error('[Claims] query failed:', err);
      setLoadError(err && err.code ? `${err.code}: ${err.message}` : ((err && err.message) || 'Failed to load claims'));
      setLoading(false);
    });
    return unsubscribe;
  }, [user?.uid, user?.role, user?.committee, user?.isAdmin, (user?.scopes || []).join('|')]);

  // RECORD 588 (ruling 202 audit): a claim filed FOR him by someone else and returned to its author is a draft he
  // cannot read (ruling 56). The return record says so - number, date, reason - and nothing of the draft.
  const [returnsToMe, setReturnsToMe] = useState([]);
  const [returnsErr, setReturnsErr] = useState('');
  useEffect(() => {
    if (!user?.uid || user?.role !== 'member') { setReturnsToMe([]); return undefined; }
    const ref = collection(db, 'claimReturns');
    const qs = [query(ref, where('claimantUid', '==', user.uid))];
    if (user.email) qs.push(query(ref, where('claimantEmail', '==', user.email)));
    const per = qs.map(() => []);
    const unsubs = qs.map((q, i) => onSnapshot(q, snap => {
      per[i] = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const m = new Map(); per.flat().forEach(r => m.set(r.id, r));
      setReturnsToMe(Array.from(m.values())); setReturnsErr('');
    }, e => setReturnsErr('Returned claims could not be read: ' + (e && e.code ? e.code : (e && e.message) || 'error'))));
    return () => unsubs.forEach(u => u());
  }, [user?.uid, user?.role, user?.email]);

  const filtered = claims.filter(c => {
    const isUnrouted = c.routingStatus === 'unrouted' || String(c.scopeKey || '').endsWith('|UNROUTED');
    const matchStage = filter === 'All'
      ? true
      : filter === 'Unrouted'
        ? isUnrouted
        : filter === 'Unheld'
          ? isUnheldSeat(c)
          : (c.status === STATUS_KEYS[filter] || c.status === filter);
    const matchSearch = !search ||
      (c.id || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.claimantName || '').toLowerCase().includes(search.toLowerCase());
    return matchStage && matchSearch;
  });

  return (
    <div>
      <div className="topbar">
        <div>
          <h1 className="topbar-title">Claims</h1>
          <div className="topbar-meta">{filtered.length} claim{filtered.length !== 1 ? 's' : ''} shown</div>
        </div>
        <div className="topbar-actions">
          <input aria-label="Search claims..."
            className="form-input"
            style={{ width: 220, padding: '7px 12px' }}
            placeholder="Search claims..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <button className="btn btn-primary" onClick={() => onNavigate('new-claim')}>+ New Claim</button>
        </div>
      </div>
      <div className="page-content">
        {scopesTruncated && (
          <div style={{ background: '#2D1A00', border: '1px solid #E8A020', borderRadius: 6, padding: '10px 14px', marginBottom: 16, color: '#E8A020', fontSize: 12 }}>
            &#9888; This account holds {user.scopes.length} territories and this list can only query 30 of them.
            Claims in the remaining {user.scopes.length - 30} are NOT shown below and this is not an empty result &mdash; tell your General Chairperson.
          </div>
        )}
        {(returnsToMe.length > 0 || returnsErr) && (
          <div style={{ background: '#1A2433', border: '1px solid #E8A020', borderRadius: 6, padding: '10px 14px', marginBottom: 16, color: '#F4F6F8', fontSize: 12 }}>
            {returnsErr ? <div style={{ color: '#FF6B6B' }}>&#9888; {returnsErr}</div> : null}
            {returnsToMe.map(r => (
              <div key={r.id} style={{ padding: '2px 0' }}>
                Claim {r.claimNumber || r.claimId}: Returned to its author on {String(r.returnedAt || '').slice(0, 10) || 'a date not recorded'} - {r.returnReason || 'no reason recorded'}
              </div>
            ))}
          </div>
        )}
        <div style={{ fontSize: 12, color: '#8AA4BF', marginBottom: 8 }}>This page's one job: see which claim needs you next. Unrouted claims reach no Local Chairperson; Unheld claims sit in a vacant seat.</div>   {/* ruling 276, screen 3 */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          {NEXT_FILTERS.map(stage => (
            <button
              key={stage}
              className={`btn btn-sm ${filter === stage ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setFilter(stage)}
            >
              {stage}
            </button>
          ))}
        </div>
        <MoreSection label="filter by stage" forceOpen={STAGE_FILTERS.indexOf(filter) !== -1} why="Open because this list is filtered by a stage.">
          <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
            {STAGE_FILTERS.map(stage => (
              <button
                key={stage}
                className={`btn btn-sm ${filter === stage ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setFilter(stage)}
              >
                {stage}
              </button>
            ))}
          </div>
        </MoreSection>
        <div className="card">
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#8AA4BF' }}>Loading claims...</div>
          ) : lcNotProvisioned ? (
            <div style={{ padding: 24, textAlign: 'center' }}>
              <div style={{ color: '#E8A020', fontWeight: 600, marginBottom: 6 }}>&#9888; This account is not provisioned for a Local Chairperson territory</div>
              <div style={{ fontSize: 12, color: '#F4F6F8' }}>Your account carries the Local Chairperson role but no territory (no scope list), so no member's claim can reach it.</div>
              <div style={{ fontSize: 11, color: '#8AA4BF', marginTop: 10 }}>This is NOT a report that there are no claims in your territory &mdash; it is a report that the platform does not yet know which territory is yours. Contact your General Chairperson to have your seat assigned.</div>
            </div>
          ) : loadError ? (
            <div style={{ padding: 24, textAlign: 'center' }}>
              <div style={{ color: '#FF6B6B', fontWeight: 600, marginBottom: 6 }}>&#9888; Couldn't load claims</div>
              <div style={{ fontSize: 12, color: '#F4A0A0', fontFamily: 'monospace' }}>{loadError}</div>
              <div style={{ fontSize: 11, color: '#8AA4BF', marginTop: 10 }}>This is a load failure, not an empty list. If it persists, a browser extension may be blocking the database connection &mdash; try an incognito window with extensions off.</div>
            </div>
          ) : (
            <div className="table-wrapper">
              {/* SECTION 507 (2026-09-29): once the page fit the window (506), this table squeezed its columns until
                  claim numbers and dates broke over several lines and Actions was cut off. A floor width keeps every
                  row readable; on a narrow window the table scrolls sideways inside its card instead. */}
              <table style={{ minWidth: 1180 }}>
                <thead>
                  <tr>
                    <th>Claim #</th>
                    <th>Claimant</th>
                    <th>Type</th>
                    <th>Committee</th>
                    <th>Status</th>
                    <th>Priority</th>
                    <th>Amount</th>
                    {/* This column renders claim.incidentDate -- the DATE OF OCCURRENCE, which is
                        the anchor computeExpiresAt() counts the filing window from. It was headed
                        "Filed" and it is not the filed date: the filed date is createdAt, read by
                        claimFiledAtSeconds() above, which this table sorts by and never displays.
                        An officer reading "Filed" against an occurrence date mis-reads the clock in
                        the one column where that matters most. Renamed to what it holds. */}
                    <th>Occurred</th>
                    {/* The stage badge says WHERE a claim is. It has never said HOW LONG it has been
                        there, and that is the one number an officer needs to run a desk. Both halves
                        are computed from data already stored -- createdAt via claimFiledAtSeconds(),
                        and expiresAt written by computeExpiresAt(). Neither is invented: an unknown
                        age prints as a dash and an absent deadline prints nothing at all. */}
                    <th>Age / Filing window</th>
                    <th>Local Chair</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={11} style={{ textAlign: 'center', color: '#4A6280', padding: 40 }}>No claims found.</td></tr>
                  ) : filtered.map(claim => (
                    <tr key={claim.docId}>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button type="button" onClick={() => onViewClaim && onViewClaim(claim.docId)}
                          aria-label={'Open claim ' + (claim.id || 'with no number')}
                          style={{ color: '#E8A020', fontWeight: 600, fontFamily: 'monospace', textDecoration: 'underline', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontSize: 'inherit' }}>
                          {claim.id}
                        </button>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{claim.claimantName}</td>
                      <td style={{ minWidth: 220 }}>{claimBasisLabel(claim) || claim.claimType || <span style={{ color: '#4A6280' }}>-</span>}</td>
                      <td style={{ whiteSpace: 'nowrap' }}><span style={{ fontSize: 11, color: '#8AA4BF' }}>{claimCommittee(claim)}</span></td>
                      <td style={{ whiteSpace: 'nowrap' }}><span className={`badge badge-${claim.status}`}>{claim.status?.replace('_', ' ')}</span></td>
                      <td style={{ whiteSpace: 'nowrap' }}><span className={`badge badge-${claim.priority}`}>{claim.priority}</span></td>
                      <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>${parseFloat(claim.amountClaimed || 0).toLocaleString()}</td>
                      <td style={{ color: '#8AA4BF', whiteSpace: 'nowrap' }}>{claim.incidentDate}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{(() => {
                        const age = daysSince(claimFiledAtSeconds(claim));
                        const left = daysUntilISO(claim.expiresAt);
                        return (
                          <span>
                            <span style={{ color: '#F4F6F8' }}>{age === null ? '-' : age + 'd'}</span>
                            {left !== null && (
                              <span style={{
                                marginLeft: 8, fontSize: 11,
                                color: left < 0 ? '#FF6B6B' : left <= 10 ? '#E8A020' : '#8AA4BF',
                              }}>
                                {left < 0 ? 'window closed ' + Math.abs(left) + 'd ago' : left + 'd left'}
                              </span>
                            )}
                            {lateArrivalFlag(claim) && (
                              <span title={lateArrivalFlag(claim).text} style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: '#0B1626', background: '#FF6B6B', borderRadius: 3, padding: '1px 5px' }}>
                                ARRIVED LATE
                              </span>
                            )}
                          </span>
                        );
                      })()}</td>
                      <td style={{ minWidth: 200, maxWidth: 260 }}>
                        {localChairFor(claim)}
                        {/* RULING 60 (2026-08-28) corrected this line the same day it shipped.
                            It read "seat unheld - no LC will see this", which said NOBODY had the
                            claim. That was wrong: where the LCA is vacant the work and the authority
                            are the General Chairperson's, solely, by operation of the General
                            Committee By-laws - and they vest automatically, with no routing action
                            by anyone. The seat being empty is a fact about the SEAT, never about
                            whether the case is being held. (VF-6: the earlier wording is withdrawn,
                            not defended.) */}
                        {isUnheldSeat(claim) && (
                          <div style={{ fontSize: 10, color: '#E8A020', marginTop: 2 }}>
                            seat vacant &mdash; General Chairperson holds this case
                          </div>
                        )}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {/* Task list section 805 (2026-10-26): pass 2 (section 796) moved the open-the-claim click from the row
                            to the claim number and left this button with no handler - it did nothing when pressed. */}
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => onViewClaim && onViewClaim(claim.docId)}>View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

